import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useTablePagination, useTableSorting, useTableFiltering, useTableSearch } from "../../../hooks";
import ChildrenTable from "../ChildrenTable";
import logger from "../../../utils/logger";

// No-op functions for URL updates (since this is in a modal)
const noOpUpdateQueryParams = () => {};
const noOpGetQueryParams = () => ({});

const SearchResults = ({ 
  children, 
  onSelectChild, 
  loading, 
  hasSearched,
  selectTransactions = false,
  showLink = true 
}) => {
  // Filter panel state
  const activeFilterColumnKey = useRef(null);
  const [isCheckPickerOpen, setIsCheckPickerOpen] = useState(false);

  // Initialize table hooks with no URL updates
  const pagination = useTablePagination({
    initialPageSize: 5,
    updateQueryParams: noOpUpdateQueryParams,
    getQueryParams: noOpGetQueryParams,
  });

  const sorting = useTableSorting({
    updateQueryParams: noOpUpdateQueryParams,
    getQueryParams: noOpGetQueryParams,
  });

  const filtering = useTableFiltering({
    initialFilters: {},
    filterableColumns: ["childId", "firstName", "surname", "familyName", "birthDate", "centreName", "statusDescription", "siblingName", "siblingChildId"],
    updateQueryParams: noOpUpdateQueryParams,
    getQueryParams: noOpGetQueryParams,
    onFiltersChange: pagination.resetToFirstPage,
  });

  const search = useTableSearch({
    searchableColumns: ["childId", "firstName", "surname", "familyName", "centreName", "statusDescription", "siblingName", "siblingChildId"],
    updateQueryParams: noOpUpdateQueryParams,
    getQueryParams: noOpGetQueryParams,
  });

  // Handle children data changes
  useEffect(() => {
    if (children.length > 0) {
      // Debug: Log the first item to see the actual data structure
      logger.debug("Sample child data structure:", children[0]);
      
      // Update column values cache for filtering
      filtering.updateColumnValuesCache(children);
      
      // Reset to first page when new data comes in
      pagination.resetToFirstPage();
    }
  }, [children]); // Only depend on children data changes

  // Process data through all table transformations
  const processedData = useMemo(() => {
    let result = [...children];

    // Apply search filter
    result = search.applySearch(result);

    // Apply filters
    result = filtering.applyDataFilters(result);

    // Apply sorting
    result = sorting.applySorting(result);

    // Apply pagination
    const paginationResult = pagination.applyPagination(result);

    return {
      paginatedData: paginationResult.paginatedData,
      totalItems: paginationResult.totalItems,
    };
  }, [
    children,
    search.searchValue,
    filtering.filtersRefreshTrigger,
    sorting.sortColumn,
    sorting.sortType,
    pagination.currentPage,
    pagination.pageSize,
  ]);

  // Filter panel handlers
  const openCheckPickerForColumn = useCallback(
    (columnKey) => {
      activeFilterColumnKey.current = columnKey;
      filtering.setFilterValues(filtering.filtersRef.current[columnKey] || []);
      setIsCheckPickerOpen(true);
    },
    [filtering]
  );

  const applyCurrentFilters = useCallback(() => {
    const selectedValuesFromPanel = filtering.getFilterValues();
    filtering.filtersRef.current = {
      ...filtering.filtersRef.current,
      [activeFilterColumnKey.current]: selectedValuesFromPanel,
    };
    filtering.applyFilters();
    setIsCheckPickerOpen(false);
  }, [filtering]);

  const onFilterChange = useCallback(
    (selectedValues) => {
      filtering.setFilterValues(selectedValues);
    },
    [filtering]
  );

  const cancelFiltering = useCallback(() => {
    setIsCheckPickerOpen(false);
  }, []);

  const getValuesForActiveColumn = useCallback(() => {
    if (!activeFilterColumnKey.current) return [];
    return filtering.getColumnValues(activeFilterColumnKey.current);
  }, [filtering]);

  // Local wrapper for sort handler so we can pass a stable function to the table
  const handleSortColumn = useCallback(
    (dataKey, newSortType) => {
      try {
        sorting.handleSortColumn(dataKey, newSortType);
        pagination.resetToFirstPage();
      } catch (err) {
        logger.error("Error handling sort change in child search modal:", err);
      }
    },
    [sorting, pagination]
  );

  if (!hasSearched) {
    return null;
  }

  return (
    <div className="search-results">
      {loading ? (
        <div className="loading-message">
          <p>Searching for children...</p>
        </div>
      ) : children.length > 0 ? (
        <>
          <div className="results-table-container">
            <ChildrenTable
              data={processedData.paginatedData}
              onSelectChild={onSelectChild}
              sortColumn={sorting.sortColumn}
              sortType={sorting.sortType}
              onSortColumn={handleSortColumn}
              loading={loading}
              activeFilters={filtering.activeFilters}
              onFilterClick={openCheckPickerForColumn}
              currentPage={pagination.currentPage}
              pageSize={pagination.pageSize}
              totalItems={processedData.totalItems}
              onPageChange={pagination.handlePageChange}
              onPageSizeChange={pagination.handlePageSizeChange}
              isCheckPickerOpen={isCheckPickerOpen}
              activeFilterColumnKey={activeFilterColumnKey.current}
              filterOptions={getValuesForActiveColumn()}
              selectedFilterValues={filtering.filtersRef.current[activeFilterColumnKey.current] || []}
              onApplyFilters={applyCurrentFilters}
              onCancelFiltering={cancelFiltering}
              onFilterChange={onFilterChange}
              selectTransactions={selectTransactions}
              showLink={showLink}
            />
          </div>
        </>
      ) : (
        <div className="no-results">
          <p>No children found matching your search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default SearchResults;